import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card } from "@bowfast/ui/components/card";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bowfast/ui/components/tabs";
import { motion } from "framer-motion";
import { BellIcon, CheckCheckIcon } from "lucide-react";
import { useState } from "react";
import { AllQuiet } from "~/components/notifications/AllQuiet";
import { SignalItem } from "~/components/notifications/SignalItem";
import { SignalPanel } from "~/components/notifications/SignalPanel";
import { SignalStrip } from "~/components/notifications/SignalStrip";
import { TestNotificationButton } from "~/components/notifications/TestNotificationButton";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { fadeUp, stagger } from "~/lib/animations";
import { useNotifications } from "~/lib/notifications-context";
import { getSignalConfig } from "./notifications.config";
import type { ActiveExpedition, Signal } from "./notifications.types";
import { groupSignals } from "./notifications.utils";

// ── Mock active expedition (no backend field for this yet) ────────────────────

const MOCK_ACTIVE_EXPEDITION: ActiveExpedition = {
  slug: "atlantic-crossing-2026",
  name: "Atlantic Crossing 2026",
  status: "active",
  dayNum: 12,
  totalDays: 21,
  crewCount: 6,
  vessel: "SY Aventura II",
  flag: "🇵🇹",
  route: "Las Palmas → Bridgetown",
};

// ── Tab ids ───────────────────────────────────────────────────────────────────

type TabId = "all" | "critical" | "urgent" | "info" | "read";

// ── NotificationsPage ─────────────────────────────────────────────────────────

export function NotificationsPage() {
  const { signals, unreadCount, isPending, isError, refetch, handleMarkRead, handleMarkAllRead } = useNotifications();

  const [activeTab, setActiveTab] = useState<TabId>("all");

  // ── Derived counts ─────────────────────────────────────────────────────────

  const now = new Date();

  const filtered: Signal[] =
    activeTab === "all"
      ? signals
      : activeTab === "read"
        ? signals.filter((s) => s.readAt)
        : signals.filter((s) => getSignalConfig(s.type).level === activeTab);

  const grouped = groupSignals(filtered, now);

  const counts = {
    all: signals.length,
    critical: signals.filter((s) => getSignalConfig(s.type).level === "critical" && !s.readAt).length,
    urgent: signals.filter((s) => getSignalConfig(s.type).level === "urgent" && !s.readAt).length,
    info: signals.filter((s) => getSignalConfig(s.type).level === "info" && !s.readAt).length,
    read: signals.filter((s) => s.readAt).length,
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-5 p-4 md:p-6">
      {/* Page header */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-500 shadow-lg shadow-abyss-500/20">
            <BellIcon className="size-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Signal Log</h1>
            <p className="text-sm text-muted-foreground">
              {isPending ? "Loading…" : unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {import.meta.env.DEV && <TestNotificationButton />}
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleMarkAllRead}>
              <CheckCheckIcon data-icon="inline-start" />
              Mark all read
            </Button>
          )}
        </div>
      </motion.div>

      {/* Pinned action strip */}
      <SignalStrip signals={signals} />

      {/* Two-column grid */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_22rem]">
        {/* Feed */}
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }}>
          {isError ? (
            <ListLoadError title="Could not load signals" onRetry={refetch} />
          ) : (
            <Card>
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
                <TabsList variant="line" className="w-full justify-start gap-0 px-5">
                  <TabsTrigger value="all">
                    All
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                      {counts.all}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="critical">
                    Critical
                    {counts.critical > 0 && (
                      <Badge variant="destructive" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                        {counts.critical}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="urgent">
                    Urgent
                    {counts.urgent > 0 && (
                      <Badge variant="invited" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                        {counts.urgent}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="info">
                    Info
                    {counts.info > 0 && (
                      <Badge variant="outline" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                        {counts.info}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="read">
                    Read
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                      {counts.read}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                {(["all", "critical", "urgent", "info", "read"] as TabId[]).map((tab) => (
                  <TabsContent key={tab} value={tab}>
                    {isPending ? (
                      <div className="divide-y">
                        {["sk-0", "sk-1", "sk-2", "sk-3"].map((key) => (
                          <div key={key} className="flex gap-3 px-5 py-4">
                            <Skeleton className="mt-0.5 size-9 shrink-0 rounded-lg" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-1/2" />
                              <Skeleton className="h-3 w-3/4" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : grouped.length === 0 ? (
                      <AllQuiet />
                    ) : (
                      <motion.div {...stagger} initial="initial" animate="animate">
                        {grouped.map((group) => (
                          <div key={group.label}>
                            <div className="flex items-center gap-3 px-5 pb-1 pt-3">
                              <span className="text-[0.6875rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                                {group.label}
                              </span>
                              <div className="h-px flex-1 bg-border" />
                            </div>
                            {group.signals.map((signal) => (
                              <SignalItem key={signal.id} signal={signal} onMarkRead={handleMarkRead} />
                            ))}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </Card>
          )}
        </motion.div>

        {/* Right panel */}
        <SignalPanel signals={signals} activeExpedition={MOCK_ACTIVE_EXPEDITION} />
      </div>
    </div>
  );
}
